import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { getLoadedStationId } from "Slices/operatorStation";
import { getCurrentSemanticMapId } from "Slices/appState";
import { useFMSWebsocket, OnlineStatusesData } from "Utils/webSocketUtils";
import {
  getMissionOperatorFleetIdForSubscribe,
  getMissionOperatorPickedFleetId,
  getOperatorMissionStatus,
  markStopReleased,
  resetMissionOperatorData,
  resetMissionOperatorState,
  setInitialMissionOperatorData,
  updateMissionOperatorData,
} from "Slices/operatorData";

import {
  setInitStationConfirmation,
  updateStationConfirmation,
} from "Slices/stationConfirmation";

import {
  MISSION_OPERATOR_WS,
  STATION_VIEW_VEHICLE_CONFIRMATION_WS,
  VEHICLE_STATE_WS,
  WS_SPINNER_LOGS,
} from "Utils/constants";
import {
  setVehiclesStateData,
  updateVehicle,
  updateVehicleOnlineStatuses,
} from "Slices/vehiclesState";
import {
  updateVehicle as updateVehicleStaticSlice,
  getVehiclesDict,
} from "Slices/vehicles";
import { convertVehicleData } from "Utils";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";

interface StationVehicleInitialData {
  sessions: StationVehicle[];
  semantic_map_id: string;
}

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:NewStationView`);

const UPDATING_FIELDS = [
  "t_long",
  "t_lat",
  "t_drive_mode",
  "t_web_status",
  "t_virtual_bumper_status",
  "t_vehicle_status",
  "last_contact",
  "sim_control_url",
  // DEV-3706: subscribe to the live online signal so the Station page can gate
  // dispatch on offline vehicles (other pages already request these fields).
  "online_status",
  "seconds_until_offline",
  // DEV-3454: request live battery so the per-vehicle state WS streams
  // `battery_level`; the backend consumer only enriches it when requested.
  "battery_level",
];

export const StationViewController = memo(() => {
  const dispatch = useDispatch();

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    [setIsWSConnecting]
  );

  const [isMapWSConnecting, setIsMapWSConnecting] = useState(false);
  const [isMissionWSConnecting, setIsMissionWSConnecting] = useState(false);
  const [
    isVehicleConfirmationWSConnecting,
    setIsVehicleConfirmationWSConnecting,
  ] = useState(false);

  useEffect(() => {
    if (
      isMapWSConnecting ||
      isMissionWSConnecting ||
      isVehicleConfirmationWSConnecting
    ) {
      wsSpinnerLogs.debug(
        "isMapWSConnecting or isMissionWSConnecting or isVehicleConfirmationWSConnecting spinner on"
      );
      setIsWSConnecting?.(true);
    } else {
      wsSpinnerLogs.debug(
        "isMapWSConnecting, isMissionWSConnecting, and isVehicleConfirmationWSConnecting spinner off"
      );
      setIsWSConnecting?.(false);
    }
  }, [
    isMapWSConnecting,
    isMissionWSConnecting,
    isVehicleConfirmationWSConnecting,
    setIsWSConnecting,
  ]);

  const vehiclesDict = useSelector(getVehiclesDict);
  const vehiclesDictRef = useRef(vehiclesDict);
  useEffect(() => {
    vehiclesDictRef.current = vehiclesDict;
  }, [vehiclesDict]);

  const stationId = useSelector(getLoadedStationId);
  const fleetIdForSubscribe = useSelector(
    getMissionOperatorFleetIdForSubscribe
  );
  const pickedFleetId = useSelector(getMissionOperatorPickedFleetId);
  const operatorMissionStatus = useSelector(getOperatorMissionStatus);
  const currentSemanticMapId = useSelector(getCurrentSemanticMapId);

  useEffect(() => {
    if (!currentSemanticMapId) {
      setIsMapWSConnecting(false);
      return;
    }

    setIsMapWSConnecting(true);
    dispatch(resetMissionOperatorData());
  }, [currentSemanticMapId, dispatch]);

  useEffect(() => {
    if (!stationId) return;
    dispatch(resetMissionOperatorState());
  }, [stationId, dispatch]);

  // Set loading state when semantic_map/state/ websocket subscription starts
  // Set to true when currentSemanticMapId is available (subscription will start)
  // Set to false when currentSemanticMapId is not available (no subscription)
  useEffect(() => {
    if (currentSemanticMapId) {
      setIsVehicleConfirmationWSConnecting(true);
    } else {
      setIsVehicleConfirmationWSConnecting(false);
    }
  }, [currentSemanticMapId]);

  const fleetId = useMemo(() => {
    if (!operatorMissionStatus) return null;
    return fleetIdForSubscribe || pickedFleetId;
  }, [fleetIdForSubscribe, operatorMissionStatus, pickedFleetId]);

  const subscriptionData = useMemo(() => {
    if (!stationId && !fleetId) return null;

    return {
      ...(stationId ? { stationId: stationId } : {}),
      ...(fleetId ? { fleetIds: [fleetId] } : {}),
    };
  }, [stationId, fleetId]);

  // Set loading state when subscription data becomes available
  useEffect(() => {
    if (subscriptionData) {
      setIsMissionWSConnecting(true);
    }
  }, [subscriptionData]);

  useFMSWebsocket({
    url: "/fleets/mission_queue/",
    subscriptionData: subscriptionData,
    loggerFlag: MISSION_OPERATOR_WS,
    getDataForSubscribe: ({ fleetIds, stationId }) => ({
      ...(fleetIds ? { fleet_ids: fleetIds } : {}),
      ...(stationId ? { missionOperatorStationId: stationId } : {}),
    }),
    getDataForUnsubscribe: ({ fleetIds, stationId }) => ({
      ...(fleetIds ? { fleet_ids: fleetIds } : {}),
      ...(stationId ? { missionOperatorStationId: stationId } : {}),
    }),
    handleInitialMessage: (data) => {
      setIsMissionWSConnecting(false);
      dispatch(setInitialMissionOperatorData(data));
    },
    handleMessage: (data) => {
      const isRunningState = data?.state === "STATE_RUNNING";
      if (isRunningState) return;

      dispatch(updateMissionOperatorData(data));
    },
    handleStopReleased: (data) => {
      dispatch(markStopReleased(data));
    },
  });

  useFMSWebsocket({
    url: "/vehicles/state/",
    subscriptionData: currentSemanticMapId,
    loggerFlag: VEHICLE_STATE_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
      fields: UPDATING_FIELDS,
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
    }),
    handleInitialMessage: (data) => {
      setIsMapWSConnecting(false);
      dispatch(setVehiclesStateData(data));
    },
    // DEV-3706: handle the standalone online_statuses broadcast (ONLINE→OFFLINE
    // transitions) so vehiclesState[*].online_status stays live on this page —
    // mirrors RealTimeMap / LiveMissionsPage.
    handleOnlineStatuses: (data: OnlineStatusesData) => {
      dispatch(updateVehicleOnlineStatuses(data));
    },
    handleMessage: (data: VehicleState) => {
      dispatch(updateVehicle(data));
      const rawId = data.vehicle_id;
      const dashedId =
        rawId && rawId.length === 32 && !rawId.includes("-")
          ? `${rawId.slice(0, 8)}-${rawId.slice(8, 12)}-${rawId.slice(
              12,
              16
            )}-${rawId.slice(16, 20)}-${rawId.slice(20)}`
          : rawId;
      const existing = vehiclesDictRef.current?.[dashedId];
      if (existing) {
        const converted = convertVehicleData(data);
        dispatch(
          updateVehicleStaticSlice({
            ...existing,
            ...converted,
            id: dashedId,
          })
        );
      }
    },
  });

  useFMSWebsocket({
    url: "/semantic_map/state/",
    subscriptionData: currentSemanticMapId,
    loggerFlag: STATION_VIEW_VEHICLE_CONFIRMATION_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_id: subscriptionId,
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_id: subscriptionId,
    }),
    handleInitialMessage: (data: StationVehicleInitialData) => {
      setIsVehicleConfirmationWSConnecting(false);
      dispatch(setInitStationConfirmation(data));
    },
    handleMessage: (data) => {
      try {
        if (!data || typeof data !== "object") {
          wsSpinnerLogs.error("Invalid message data received");
          return;
        }
        dispatch(updateStationConfirmation(data));
      } catch (error) {
        wsSpinnerLogs.error(
          "Error processing WebSocket message:",
          error instanceof Error ? error.message : String(error)
        );
      }
    },
  });

  return null;
});
